/**
 * Les réglages d'une expérience.
 *
 * Rien n'est codé en dur ici : le rendu et la liaison parcourent la liste de
 * contrôles que l'expérience fournit. Ce module ne sait pas ce qu'il règle —
 * ni gnomon, ni pesanteur — et c'est ce qui lui permet de servir toutes les
 * expériences.
 *
 * Deux groupes, et la distinction porte du sens. **La situation** décrit les
 * données du problème : ce ne sont pas des hypothèses, et tous les modèles les
 * reçoivent à l'identique. **Les hypothèses libres** sont ce qu'un modèle
 * réclame en plus pour fonctionner — celui qui n'en réclame aucune n'a aucun
 * curseur, et c'est précisément ce que le comparateur compte.
 *
 * L'ajustement reste manuel par construction : nulle part une fonction ne
 * cherche les valeurs qui feraient coller un modèle aux mesures (CLAUDE.md §4).
 * Des `<input type="range">` natifs, donc atteignables au clavier sans une
 * ligne de JavaScript (CLAUDE.md §3).
 */

import type { ControlSpec, Preset } from "../experiment/types.js";
import { quantity, valueIn } from "../core/units.js";
import { escape, escapeAttribute, formatNumber, unitLabel } from "./format.js";

function display<S>(spec: ControlSpec<S>, settings: S): string {
  if (spec.readout !== undefined) return spec.readout(settings);
  const value = valueIn(spec.read(settings), spec.unit);
  // Le sans-dimension porte l'unité « 1 » : la donner à lire n'apprendrait rien.
  const unit = spec.unit === "1" ? "" : ` ${unitLabel(spec.unit)}`;
  return `${formatNumber(value, spec.decimals)}${unit}`;
}

function field<S>(spec: ControlSpec<S>, settings: S): string {
  const value = valueIn(spec.read(settings), spec.unit);
  const id = `verlet-control-${spec.id}`;
  return [
    `<div class="verlet-field">`,
    `<label for="${escapeAttribute(id)}">${escape(spec.label)}</label>`,
    `<output for="${escapeAttribute(id)}" data-readout="${escapeAttribute(spec.id)}">`,
    `${escape(display(spec, settings))}</output>`,
    `<input type="range" id="${escapeAttribute(id)}" data-setting="${escapeAttribute(spec.id)}"`,
    ` min="${spec.min}" max="${spec.max}" step="${spec.step}" value="${value}"`,
    ` aria-valuetext="${escapeAttribute(display(spec, settings))}"`,
    ` aria-describedby="${escapeAttribute(`${id}-hint`)}" />`,
    `<p class="verlet-field__hint" id="${escapeAttribute(`${id}-hint`)}">${escape(spec.hint)}</p>`,
    `</div>`,
  ].join("");
}

function group<S>(specs: readonly ControlSpec<S>[], settings: S): string {
  return `<div class="verlet-fields">${specs.map((spec) => field(spec, settings)).join("")}</div>`;
}

export function renderSituationControls<S>(
  controls: readonly ControlSpec<S>[],
  presets: readonly Preset<S>[],
  presetsLabel: string,
  caveat: string,
  settings: S,
): string {
  const buttons = presets
    .map(
      (preset) =>
        `<button type="button" class="verlet-toggle" data-preset="${escapeAttribute(
          preset.id,
        )}">${escape(preset.label)}</button>`,
    )
    .join("");

  const picker =
    presets.length === 0
      ? ""
      : [
          `<div class="verlet-toggles" role="group"`,
          ` aria-label="${escapeAttribute(presetsLabel)}">${buttons}</div>`,
        ].join("");

  return [
    `<div class="verlet-controls">`,
    `<p class="verlet-caveat" role="note">${escape(caveat)}</p>`,
    picker,
    group(
      controls.filter((spec) => !spec.freeHypothesis),
      settings,
    ),
    `</div>`,
  ].join("");
}

export function renderFreeHypothesisControls<S>(
  controls: readonly ControlSpec<S>[],
  caveat: string,
  settings: S,
): string {
  return [
    `<div class="verlet-controls">`,
    `<p class="verlet-caveat" role="note">${escape(caveat)}</p>`,
    group(
      controls.filter((spec) => spec.freeHypothesis),
      settings,
    ),
    `</div>`,
  ].join("");
}

/**
 * Branche tous les contrôles. L'état courant est relu à chaque événement via
 * `current()` plutôt que capturé : un curseur bougé après un changement de
 * réglage doit partir de l'état d'alors, pas de celui du montage.
 */
export function bindControls<S>(
  root: ParentNode,
  controls: readonly ControlSpec<S>[],
  presets: readonly Preset<S>[],
  current: () => S,
  onChange: (settings: S) => void,
): void {
  const specs = new Map(controls.map((spec) => [spec.id, spec]));

  const refresh = (settings: S): void => {
    for (const spec of controls) {
      const readout = root.querySelector(`[data-readout="${spec.id}"]`);
      if (readout !== null) readout.textContent = display(spec, settings);
      const input = root.querySelector<HTMLInputElement>(`[data-setting="${spec.id}"]`);
      if (input !== null) {
        const value = String(valueIn(spec.read(settings), spec.unit));
        if (input.value !== value) input.value = value;
        input.setAttribute("aria-valuetext", display(spec, settings));
      }
    }
  };

  for (const input of root.querySelectorAll<HTMLInputElement>("[data-setting]")) {
    input.addEventListener("input", () => {
      const spec = specs.get(input.dataset["setting"] ?? "");
      if (spec === undefined) return;
      const value = Number.parseFloat(input.value);
      if (!Number.isFinite(value)) return;
      const next = spec.write(current(), quantity(value, spec.unit));
      refresh(next);
      onChange(next);
    });
  }

  const byId = new Map(presets.map((preset) => [preset.id, preset]));
  for (const button of root.querySelectorAll<HTMLButtonElement>("[data-preset]")) {
    button.addEventListener("click", () => {
      const preset = byId.get(button.dataset["preset"] ?? "");
      if (preset === undefined) return;
      const next = preset.apply(current());
      refresh(next);
      onChange(next);
    });
  }
}
